import React, { useState, useEffect, useCallback } from 'react';
import { marked } from 'marked';
import api from '../utils/api';
import toast from 'react-hot-toast';

// Configure marked
marked.setOptions({ breaks: true, gfm: true });

const categoryLabels = {
  'dasar-pbj': 'Dasar PBJ', perencanaan: 'Perencanaan', 'pemilihan-penyedia': 'Pemilihan Penyedia',
  'manajemen-kontrak': 'Manajemen Kontrak', 'pengadaan-khusus': 'Pengadaan Khusus',
  eprocurement: 'E-Procurement', 'pengaduan-sanksi': 'Pengaduan & Sanksi', sertifikasi: 'Sertifikasi'
};
const difficultyLabels = { pemula: 'Pemula', menengah: 'Menengah', lanjut: 'Lanjut' };
const difficultyColors = { pemula: 'badge-green', menengah: 'badge-yellow', lanjut: 'badge-red' };

const LearningPage = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState('');
  const [filterDiff, setFilterDiff] = useState('');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseDetail, setCourseDetail] = useState(null);
  const [activeModule, setActiveModule] = useState(null);
  const [quizMode, setQuizMode] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);

  const fetchCourses = useCallback(async () => {
    try {
      const params = {};
      if (filterCat) params.category = filterCat;
      if (filterDiff) params.difficulty = filterDiff;
      const { data } = await api.get('/learning/courses', { params });
      setCourses(data.courses);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, [filterCat, filterDiff]);

  useEffect(() => { fetchCourses(); }, [fetchCourses]);

  const openCourse = async (id) => {
    try {
      const { data } = await api.get(`/learning/courses/${id}`);
      setCourseDetail(data.course);
      setSelectedCourse(id);
      if (data.course.modules?.length > 0) {
        setActiveModule(data.course.modules[0]);
      }
    } catch (err) { toast.error('Gagal memuat kursus'); }
  };

  const markComplete = async (moduleId, score) => {
    try {
      await api.post('/learning/progress', {
        module_id: moduleId,
        course_id: selectedCourse,
        completed: true,
        score: score !== undefined ? score : null
      });
      toast.success('Modul ditandai selesai!');
      // Refresh course detail tapi pertahankan activeModule yang sedang dipilih
      const { data } = await api.get(`/learning/courses/${selectedCourse}`);
      setCourseDetail(data.course);
      // Update activeModule dengan data terbaru dari server (bukan reset ke modul pertama)
      const currentModuleId = moduleId;
      const refreshed = data.course.modules?.find(m => m.id === currentModuleId);
      if (refreshed) setActiveModule(refreshed);
    } catch (err) { /* ignore */ }
  };

  const handleQuizSubmit = () => {
    setQuizSubmitted(true);
    const quizzes = activeModule?.quizzes || [];
    const correct = quizzes.filter(q => quizAnswers[q.id] === q.correct_answer).length;
    const total = quizzes.length;
    const score = total > 0 ? Math.round((correct / total) * 100) : 0;
    if (correct === total && total > 0) {
      markComplete(activeModule.id, score);
    }
  };

  // Course detail view
  if (selectedCourse && courseDetail) {
    return (
      <div className="fade-in">
        <button className="btn btn-ghost btn-sm" onClick={() => { setSelectedCourse(null); setCourseDetail(null); setActiveModule(null); setQuizMode(false); setQuizAnswers({}); setQuizSubmitted(false); }} style={{ marginBottom: 20 }}>
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" /></svg>
          Kembali ke Daftar Kursus
        </button>

        <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 24 }}>
          {/* Sidebar modules */}
          <div className="card" style={{ padding: 16, height: 'fit-content', position: 'sticky', top: 'calc(var(--header-height) + 32px)' }}>
            <h3 style={{ fontSize: '0.875rem', fontWeight: 700, marginBottom: 12, color: 'var(--text-secondary)' }}>Modul</h3>
            {courseDetail.modules?.map((mod, i) => (
              <button
                key={mod.id}
                onClick={() => { setActiveModule(mod); setQuizMode(false); setQuizAnswers({}); setQuizSubmitted(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                  padding: '10px 12px', borderRadius: 'var(--radius-md)',
                  background: activeModule?.id === mod.id ? 'var(--accent-soft)' : 'transparent',
                  border: 'none', cursor: 'pointer', textAlign: 'left',
                  color: activeModule?.id === mod.id ? 'var(--accent-primary)' : 'var(--text-secondary)',
                  fontFamily: 'var(--font-sans)', fontSize: '0.8125rem', fontWeight: activeModule?.id === mod.id ? 600 : 500,
                  marginBottom: 4, transition: 'all var(--transition-fast)'
                }}
              >
                <div style={{
                  width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                  border: `2px solid ${mod.completed ? 'var(--success)' : activeModule?.id === mod.id ? 'var(--accent-primary)' : 'var(--border-primary)'}`,
                  background: mod.completed ? 'var(--success)' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', fontSize: '0.6875rem', fontWeight: 700
                }}>
                  {mod.completed ? '✓' : i + 1}
                </div>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{mod.title}</span>
              </button>
            ))}
          </div>

          {/* Content */}
          <div>
            {activeModule && !quizMode && (
              <div className="card">
                {activeModule.thumbnail_url && (
                  <div style={{ marginBottom: 20, borderRadius: 'var(--radius-md)', overflow: 'hidden', maxHeight: 280 }}>
                    <img src={activeModule.thumbnail_url} alt={activeModule.title}
                      style={{ width: '100%', objectFit: 'cover', maxHeight: 280, display: 'block' }}
                      onError={e => { e.target.style.display='none'; }} />
                  </div>
                )}
                <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: 8 }}>{activeModule.title}</h2>
                <p style={{ fontSize: '0.8125rem', color: 'var(--text-tertiary)', marginBottom: 20 }}>
                  {activeModule.duration_minutes} menit • {activeModule.content_type === 'text' ? 'Materi Teks' : activeModule.content_type}
                </p>
                <div
                  style={{ fontSize: '0.9375rem', lineHeight: 1.8, color: 'var(--text-primary)' }}
                  dangerouslySetInnerHTML={{ __html: marked(activeModule.content || '') }}
                  className="content-html"
                />
                <div style={{ display: 'flex', gap: 12, marginTop: 28, paddingTop: 20, borderTop: '1px solid var(--border-secondary)', flexWrap: 'wrap', alignItems: 'center' }}>
                  {activeModule.quizzes?.length > 0 && (
                    <button className="btn btn-primary" onClick={() => { setQuizMode(true); setQuizAnswers({}); setQuizSubmitted(false); }}>
                      <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" /></svg>
                      Ikuti Quiz ({activeModule.quizzes.length} soal)
                    </button>
                  )}
                  {!activeModule.completed && (
                    <button className="btn btn-secondary" onClick={() => markComplete(activeModule.id, 0)}>
                      Tandai Selesai
                    </button>
                  )}
                  {activeModule.completed && (
                    <span style={{ fontSize: '0.8125rem', color: 'var(--success)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      Modul selesai
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Quiz mode */}
            {quizMode && activeModule?.quizzes && (
              <div className="card">
                <h2 style={{ fontSize: '1.125rem', fontWeight: 700, marginBottom: 20 }}>
                  Quiz: {activeModule.title}
                </h2>
                {activeModule.quizzes.map((q, qi) => (
                  <div key={q.id} style={{ marginBottom: 24, paddingBottom: 24, borderBottom: qi < activeModule.quizzes.length - 1 ? '1px solid var(--border-secondary)' : 'none' }}>
                    {q.image_url && (
                      <div style={{ marginBottom: 12, borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                        <img src={q.image_url} alt="Gambar soal"
                          style={{ maxWidth: '100%', maxHeight: 240, objectFit: 'contain', borderRadius: 'var(--radius-md)', background: 'var(--bg-tertiary)' }}
                          onError={e => { e.target.style.display='none'; }} />
                      </div>
                    )}
                    <p style={{ fontWeight: 600, marginBottom: 12, fontSize: '0.9375rem' }}>
                      {qi + 1}. {q.question}
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {q.options.map((opt, oi) => {
                        const isSelected = quizAnswers[q.id] === oi;
                        const isCorrect = quizSubmitted && oi === q.correct_answer;
                        const isWrong = quizSubmitted && isSelected && oi !== q.correct_answer;
                        return (
                          <button
                            key={oi}
                            onClick={() => !quizSubmitted && setQuizAnswers(p => ({ ...p, [q.id]: oi }))}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 12,
                              padding: '12px 16px', borderRadius: 'var(--radius-md)',
                              border: `2px solid ${isCorrect ? 'var(--success)' : isWrong ? 'var(--error)' : isSelected ? 'var(--accent-primary)' : 'var(--border-primary)'}`,
                              background: isCorrect ? 'rgba(34,197,94,0.1)' : isWrong ? 'rgba(239,68,68,0.1)' : isSelected ? 'var(--accent-soft)' : 'var(--bg-secondary)',
                              cursor: quizSubmitted ? 'default' : 'pointer',
                              fontSize: '0.875rem', textAlign: 'left', fontFamily: 'var(--font-sans)',
                              color: 'var(--text-primary)', transition: 'all var(--transition-fast)'
                            }}
                          >
                            <span style={{
                              width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                              border: `2px solid ${isSelected ? 'var(--accent-primary)' : 'var(--border-primary)'}`,
                              background: isSelected ? 'var(--accent-primary)' : 'transparent',
                              display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                              {isSelected && <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'white' }} />}
                            </span>
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                    {quizSubmitted && q.explanation && (
                      <div style={{ marginTop: 10, padding: 12, borderRadius: 'var(--radius-sm)', background: 'var(--bg-tertiary)', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                        <strong>Penjelasan:</strong> {q.explanation}
                      </div>
                    )}
                  </div>
                ))}
                {quizSubmitted && (() => {
                  const total = activeModule.quizzes.length;
                  const correct = activeModule.quizzes.filter(q => quizAnswers[q.id] === q.correct_answer).length;
                  const pct = Math.round((correct / total) * 100);
                  const passed = pct >= 70;
                  return (
                    <div style={{
                      padding: '20px 24px', borderRadius: 'var(--radius-md)', marginBottom: 16,
                      background: passed ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                      border: `1px solid ${passed ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                        <div style={{
                          width: 48, height: 48, borderRadius: '50%',
                          background: passed ? 'linear-gradient(135deg, #22c55e, #16a34a)' : 'linear-gradient(135deg, #ef4444, #dc2626)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: 'white', fontWeight: 800, fontSize: '1.125rem'
                        }}>{pct}%</div>
                        <div>
                          <h4 style={{ fontWeight: 700, color: passed ? '#16a34a' : '#dc2626', marginBottom: 2 }}>
                            {passed ? '🎉 Selamat! Quiz Lulus' : '📝 Belum Lulus'}
                          </h4>
                          <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                            Jawaban benar: {correct} dari {total} soal
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })()}
                <div style={{ display: 'flex', gap: 12 }}>
                  {!quizSubmitted ? (
                    <button className="btn btn-primary" onClick={handleQuizSubmit}
                      disabled={Object.keys(quizAnswers).length < activeModule.quizzes.length}>
                      Kirim Jawaban
                    </button>
                  ) : (
                    <>
                      <button className="btn btn-primary" onClick={() => { setQuizAnswers({}); setQuizSubmitted(false); }}>
                        Coba Lagi
                      </button>
                      <button className="btn btn-secondary" onClick={() => { setQuizMode(false); setQuizAnswers({}); setQuizSubmitted(false); }}>
                        Kembali ke Materi
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <style>{`
          .content-html h2 { font-size: 1.25rem; font-weight: 700; margin: 24px 0 12px; color: var(--text-primary); }
          .content-html h3 { font-size: 1.0625rem; font-weight: 600; margin: 20px 0 10px; color: var(--text-primary); }
          .content-html p { margin-bottom: 12px; }
          .content-html ul, .content-html ol { padding-left: 24px; margin-bottom: 12px; }
          .content-html li { margin-bottom: 6px; }
          .content-html strong { font-weight: 600; color: var(--text-accent); }
          .content-html table { width: 100%; border-collapse: collapse; margin: 16px 0; }
          .content-html th, .content-html td { padding: 10px 14px; border: 1px solid var(--border-primary); text-align: left; }
          .content-html th { background: var(--bg-tertiary); font-weight: 600; }
          @media (max-width: 768px) {
            div[style*="grid-template-columns: 280px"] { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </div>
    );
  }

  // Course list
  return (
    <div className="fade-in">
      {/* Filters */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div className="form-group" style={{ flex: '1 1 200px' }}>
            <label className="form-label">Kategori</label>
            <select className="form-select" value={filterCat} onChange={(e) => setFilterCat(e.target.value)}>
              <option value="">Semua Kategori</option>
              {Object.entries(categoryLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ flex: '0 1 160px' }}>
            <label className="form-label">Tingkat</label>
            <select className="form-select" value={filterDiff} onChange={(e) => setFilterDiff(e.target.value)}>
              <option value="">Semua</option>
              {Object.entries(difficultyLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading-screen"><div className="spinner" /><span>Memuat kursus...</span></div>
      ) : courses.length === 0 ? (
        <div className="empty-state">
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5" width="48" height="48"><path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347" /></svg>
          <p>Belum ada kursus tersedia</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
          {courses.map((course) => (
            <div key={course.id} className="card" style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column' }} onClick={() => openCourse(course.id)}>
              {course.thumbnail ? (
                <div style={{
                  height: 140, borderRadius: 'var(--radius-md)', marginBottom: 16, overflow: 'hidden', background: 'var(--bg-tertiary)'
                }}>
                  <img src={course.thumbnail} alt={course.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                  <div style={{
                    display: 'none', height: '100%', alignItems: 'center', justifyContent: 'center',
                    background: `linear-gradient(135deg, var(--orange-${course.difficulty === 'pemula' ? '400' : course.difficulty === 'menengah' ? '500' : '700'}), var(--gray-800))`
                  }}>
                    <svg width="48" height="48" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="1.5" opacity="0.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342" />
                    </svg>
                  </div>
                </div>
              ) : (
                <div style={{
                  height: 140, borderRadius: 'var(--radius-md)', marginBottom: 16,
                  background: `linear-gradient(135deg, var(--orange-${course.difficulty === 'pemula' ? '400' : course.difficulty === 'menengah' ? '500' : '700'}), var(--gray-800))`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <svg width="48" height="48" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="1.5" opacity="0.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342" />
                  </svg>
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                <span className="badge badge-orange">{categoryLabels[course.category]}</span>
                <span className={`badge ${difficultyColors[course.difficulty]}`}>{difficultyLabels[course.difficulty]}</span>
              </div>

              <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 8, lineHeight: 1.4 }}>{course.title}</h3>
              <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.6, flex: 1 }}>
                {course.description?.slice(0, 120)}{course.description?.length > 120 ? '...' : ''}
              </p>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border-secondary)' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                  {course.total_modules || course.modules?.length || 0} modul • {course.duration_minutes} menit
                </span>
                {course.progress_percent > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 60, height: 4, borderRadius: 2, background: 'var(--bg-tertiary)', overflow: 'hidden' }}>
                      <div style={{ width: `${course.progress_percent}%`, height: '100%', background: 'var(--accent-primary)', borderRadius: 2 }} />
                    </div>
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-accent)' }}>{course.progress_percent}%</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LearningPage;
